/* eslint-disable @typescript-eslint/no-explicit-any */

export type JsonSchema = Record<string, any>;

export const STRING_OR_ENUM_ARRAY_UNION = 'x-string-or-enum-array-union';

export const getOneOfVariants = (schema: JsonSchema | undefined): JsonSchema[] | undefined => {

  if (!schema) {

    return undefined;

  }
  const union = schema.oneOf ?? schema.anyOf;
  if (!Array.isArray(union) || union.length === 0) {

    return undefined;

  }
  return union;

};

const isObjectVariant = (variant: JsonSchema): boolean => (
  variant?.type === 'object' && variant.properties && typeof variant.properties === 'object'
);

const allObjectVariantsWithProperties = (variants: JsonSchema[]): boolean => (
  variants.length > 0 && variants.every(isObjectVariant)
);

const getSingleEnumValue = (propSchema: JsonSchema | undefined): string | undefined => {

  if (!propSchema || propSchema.type !== 'string' || !Array.isArray(propSchema.enum) || propSchema.enum.length !== 1) {

    return undefined;

  }
  return propSchema.enum[0];

};

export const isDiscriminatedObjectOneOf = (
  variants: JsonSchema[],
  discriminator = 'type',
): boolean => {

  if (!allObjectVariantsWithProperties(variants)) {

    return false;

  }
  return variants.every((variant) => getSingleEnumValue(variant.properties?.[discriminator]) !== undefined);

};

export const pickOneOfVariant = (
  variants: JsonSchema[],
  discriminator: string,
  value: unknown,
): JsonSchema | undefined => {

  if (value === undefined || value === null || value === '') {

    return undefined;

  }
  return variants.find((variant) => getSingleEnumValue(variant.properties?.[discriminator]) === value);

};

const mergeEnumValues = (left: JsonSchema | undefined, right: JsonSchema | undefined): JsonSchema | undefined => {

  const leftEnum = left?.enum;
  const rightEnum = right?.enum;
  if (!Array.isArray(leftEnum) && !Array.isArray(rightEnum)) {

    return undefined;

  }
  const merged = [...new Set([...(leftEnum ?? []), ...(rightEnum ?? [])])];
  return {
    ...(left ?? right),
    type: left?.type ?? right?.type,
    enum: merged,
  };

};

function mergedRequiredFromVariants(variants: JsonSchema[]): string[] {

  const allKeys = new Set<string>();
  for (const variant of variants) {

    for (const key of Object.keys(variant.properties ?? {})) {

      allKeys.add(key);

    }

  }
  return [...allKeys].filter((key) => {

    const variantsWithKey = variants.filter((variant) => variant.properties?.[key]);
    return variantsWithKey.length > 0 && variantsWithKey.every((variant) => variant.required?.includes(key));

  });

}

const isIdenticalPrimitiveOneOf = (variants: JsonSchema[]): boolean => {

  if (variants.length === 0) {

    return false;

  }
  const firstType = variants[0].type;
  if (!firstType || firstType === 'object' || firstType === 'array') {

    return false;

  }
  return variants.every((variant) => variant.type === firstType);

};

const isStringOrEnumArrayUnion = (variants: JsonSchema[]): boolean => {

  if (variants.length !== 2) {

    return false;

  }
  const stringVariant = variants.find((variant) => variant.type === 'string' && Array.isArray(variant.enum));
  const arrayVariant = variants.find((variant) => (
    variant.type === 'array'
    && variant.items?.type === 'string'
    && Array.isArray(variant.items?.enum)
  ));
  if (!stringVariant || !arrayVariant) {

    return false;

  }
  const stringEnum = [...stringVariant.enum].sort().join(',');
  const arrayEnum = [...arrayVariant.items.enum].sort().join(',');
  return stringEnum === arrayEnum;

};

export function resolveSchemaForForm(schema: JsonSchema | undefined): JsonSchema | undefined {

  if (!schema) {

    return undefined;

  }

  const variants = getOneOfVariants(schema);
  if (variants) {

    if (allObjectVariantsWithProperties(variants)) {

      // mergeOneOfObjectVariants is defined below; mutual recursion via function declarations
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return mergeOneOfObjectVariants(variants);

    }
    if (isIdenticalPrimitiveOneOf(variants)) {

      return { ...variants[0] };

    }
    if (isStringOrEnumArrayUnion(variants)) {

      const stringVariant = variants.find((variant) => variant.type === 'string');
      if (!stringVariant) {

        return schema;

      }
      return {
        ...schema,
        oneOf: undefined,
        anyOf: undefined,
        [STRING_OR_ENUM_ARRAY_UNION]: variants,
        type: 'string',
        enum: stringVariant.enum,
        'x-optional': schema['x-optional'],
      };

    }
    return schema;

  }

  // Resolve this level only: nested items/properties are resolved again as they are rendered or
  // stripped, where the item value is known. Merging them here would inject a variant-only
  // property (e.g. `options` on a `select` field) into every sibling variant.
  return { ...schema };

}

export function mergePropertySchemas(left: JsonSchema, right: JsonSchema): JsonSchema {

  const leftResolved = resolveSchemaForForm(left) ?? left;
  const rightResolved = resolveSchemaForForm(right) ?? right;

  if (leftResolved.type === rightResolved.type && leftResolved.type === 'object') {

    const properties: Record<string, JsonSchema> = { ...leftResolved.properties };
    for (const [key, propSchema] of Object.entries(rightResolved.properties ?? {})) {

      const resolvedPropSchema = propSchema as JsonSchema;
      if (properties[key]) {

        properties[key] = mergePropertySchemas(properties[key], resolvedPropSchema);

      } else {

        properties[key] = resolvedPropSchema;

      }

    }
    const required = mergedRequiredFromVariants([
      { properties, required: leftResolved.required },
      { properties, required: rightResolved.required },
    ]);
    return {
      type: 'object',
      properties,
      required,
    };

  }

  if (leftResolved.type === 'array' && rightResolved.type === 'array') {

    return {
      ...leftResolved,
      ...rightResolved,
      type: 'array',
      items: mergePropertySchemas(leftResolved.items ?? {}, rightResolved.items ?? {}),
    };

  }

  const mergedEnum = mergeEnumValues(leftResolved, rightResolved);
  if (mergedEnum) {

    return mergedEnum;

  }

  if (leftResolved.type && leftResolved.type === rightResolved.type) {

    return { ...leftResolved, ...rightResolved, type: leftResolved.type };

  }

  return resolveSchemaForForm({ oneOf: [leftResolved, rightResolved] }) ?? leftResolved;

}

export function mergeOneOfObjectVariants(variants: JsonSchema[]): JsonSchema {

  const resolvedVariants = variants
    .map((variant) => resolveSchemaForForm(variant) ?? variant)
    .filter(isObjectVariant);
  const properties: Record<string, JsonSchema> = {};

  for (const variant of resolvedVariants) {

    for (const [key, propSchema] of Object.entries(variant.properties ?? {})) {

      const resolvedPropSchema = (resolveSchemaForForm(propSchema as JsonSchema) ?? propSchema) as JsonSchema;
      if (properties[key]) {

        properties[key] = mergePropertySchemas(properties[key], resolvedPropSchema);

      } else {

        properties[key] = resolvedPropSchema;

      }

    }

  }

  if (isDiscriminatedObjectOneOf(resolvedVariants)) {

    const discriminator = 'type';
    const typeEnums = resolvedVariants
      .map((variant) => getSingleEnumValue(variant.properties?.[discriminator]))
      .filter((value): value is string => value !== undefined);
    properties[discriminator] = {
      type: 'string',
      enum: [...new Set(typeEnums)],
    };

  }

  return {
    type: 'object',
    properties,
    required: mergedRequiredFromVariants(resolvedVariants),
  };

}
